import { useState, useEffect } from 'react';
import { bookData, stats } from '../../../mocks/bookData';
import BookPreview from '../../../components/feature/BookPreview';
import { useScrollReveal, useCountUp } from '../../../hooks/useScrollReveal';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);
  const statsReveal = useScrollReveal({ threshold: 0.3 });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setParallaxY(window.scrollY * 0.5);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Count-up animations for stats
  const stat1 = useCountUp(parseInt(stats[0].value), 2000, statsReveal.isVisible);
  const stat2 = useCountUp(parseInt(stats[1].value), 2000, statsReveal.isVisible);
  const stat3 = useCountUp(parseInt(stats[2].value), 2000, statsReveal.isVisible);
  const stat4 = useCountUp(parseInt(stats[3].value), 2000, statsReveal.isVisible);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-950 via-purple-900 to-indigo-900 overflow-hidden">
      {/* Book Preview Modal */}
      <BookPreview 
        isOpen={isPreviewOpen} 
        onClose={() => setIsPreviewOpen(false)} 
        language="lt"
      />

      {/* Animated particles background */}
      <div className="particles-bg" style={{ transform: `translateY(${parallaxY}px)` }}>
        <div className="particle" style={{ width: '120px', height: '120px', top: '10%', left: '5%', animationDelay: '0s' }}></div>
        <div className="particle" style={{ width: '80px', height: '80px', top: '20%', right: '10%', animationDelay: '1s' }}></div>
        <div className="particle" style={{ width: '100px', height: '100px', bottom: '15%', left: '15%', animationDelay: '2s' }}></div>
        <div className="particle" style={{ width: '60px', height: '60px', top: '50%', right: '20%', animationDelay: '1.5s' }}></div>
      </div>

      {/* Animated background elements with parallax */}
      <div className="absolute inset-0 overflow-hidden" style={{ transform: `translateY(${parallaxY * 0.3}px)` }}>
        <div className="absolute top-20 left-10 w-32 h-32 text-amber-400/10 animate-float">
          <i className="ri-star-line text-6xl"></i>
        </div>
        <div className="absolute top-40 right-20 w-24 h-24 text-purple-300/10 animate-float-delayed">
          <i className="ri-moon-line text-5xl"></i>
        </div>
        <div className="absolute bottom-40 left-1/4 w-28 h-28 text-pink-300/10 animate-float">
          <i className="ri-planet-line text-5xl"></i>
        </div>
        <div className="absolute top-1/3 right-1/3 w-20 h-20 text-amber-300/10 animate-float-delayed">
          <i className="ri-sparkling-2-line text-4xl"></i>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={bookData.logoImage} alt="Keturi Vartai į Save logotipas" className="h-12 w-12 object-contain" loading="lazy" />
              <span className={`font-serif text-xl font-bold ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                KETURI VARTAI Į SAVE
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection('about')}
                className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/90 hover:text-white'}`}
              >
                Apie Knygą
              </button>
              <button 
                onClick={() => scrollToSection('features')}
                className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/90 hover:text-white'}`}
              >
                Ką Gausite
              </button>
              <button 
                onClick={() => scrollToSection('testimonials')}
                className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/90 hover:text-white'}`}
              >
                Atsiliepimai
              </button>
              <a 
                href="/contact"
                className={`font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/90 hover:text-white'}`}
              >
                Kontaktai
              </a>
              <a
                href="/en"
                className={`flex items-center gap-1.5 font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-white/90 hover:text-white'}`}
              >
                <span className="text-lg">🇬🇧</span>
                <span>EN</span>
              </a>
              <button 
                onClick={() => scrollToSection('purchase')}
                className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 px-6 py-2.5 rounded-full font-semibold hover:from-amber-500 hover:to-amber-600 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                ĮSIGYTI DABAR
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Text content with staggered animations */}
          <div className="text-white space-y-8">
            <div className="inline-block opacity-0 animate-fade-in-up stagger-1">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 text-white px-4 py-2 rounded-full text-sm font-semibold">
                BESTSELERIS 2025
              </span>
            </div>
            
            <h1 className="font-serif text-6xl lg:text-7xl font-bold leading-tight">
              <span className="block opacity-0 animate-fade-in-up stagger-2">Atrask Savo</span>
              <span className="block opacity-0 animate-fade-in-up stagger-3">Tikrąjį Kelią</span>
              <span className="block opacity-0 animate-fade-in-up stagger-4">Per Žvaigždes</span>
            </h1>
            
            <p className="text-xl text-white/90 leading-relaxed max-w-xl opacity-0 animate-fade-in-up stagger-5">
              Ar kada jautei, kad gyveni „kaip reikia", bet viduje kažkas tyli?
Kad bandai susitvarkyti gyvenimą protu, bet trūksta aiškumo, jėgos ar vidinės ramybės?
            </p>

            {/* Bullet points with staggered animation */}
            <div className="space-y-4 pl-2">
              <div className="flex items-start gap-4 opacity-0 animate-fade-in-left stagger-6">
                <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-white/95 text-lg leading-relaxed">
                  Atrasi, kurios energijos tau dabar labiausiai trūksta (Ugnies / Oro / Vandens / Žemės)
                </p>
              </div>
              <div className="flex items-start gap-4 opacity-0 animate-fade-in-left stagger-7">
                <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-white/95 text-lg leading-relaxed">
                  Sužinosi kaip grįžti į balansą per paprastus veiksmus (15–25 min per dieną)
                </p>
              </div>
              <div className="flex items-start gap-4 opacity-0 animate-fade-in-left stagger-8">
                <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-white/95 text-lg leading-relaxed">
                  Pradėsi suprasti ne tik save, bet ir partnerį – ir kalbėti "tiesiai į 10-tuką"
                </p>
              </div>
            </div>
            
            <div className="space-y-4 pt-4 opacity-0 animate-fade-in-up stagger-8">
              <button 
                onClick={() => scrollToSection('purchase')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all shadow-xl flex items-center gap-3 group whitespace-nowrap cursor-pointer animate-pulse-glow"
              >
                PRADĖTI TRANSFORMACIJĄ
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </button>
              <p className="text-white/80 text-sm">
                Tik {bookData.price}{bookData.currency} | Momentinis pristatymas
              </p>
            </div>
            
            <div className="flex items-center gap-2 pt-4 opacity-0 animate-fade-in-up stagger-8">
              <div className="flex text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl"></i>
                ))}
              </div>
              <span className="text-white/90">
                {bookData.rating}/5 |&nbsp; Žmonės dažniausiai sako: „Pagaliau supratau, nuo ko pradėti."
              </span>
            </div>
          </div>

          {/* Right side - Book visualization with 3D tilt effect */}
          <div className="relative opacity-0 animate-scale-in stagger-4">
            <div className="relative z-10 hover-tilt">
              <img 
                src={bookData.coverImage} 
                alt="Keturi Vartai į Save knyga - astrologijos ir savęs pažinimo vadovas"
                className="w-full max-w-md mx-auto rounded-lg shadow-2xl"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-500/20 via-transparent to-transparent rounded-lg"></div>
              
              {/* Preview button */}
              <button
                onClick={() => setIsPreviewOpen(true)}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/95 hover:bg-white text-gray-900 px-6 py-3 rounded-full font-semibold shadow-xl flex items-center gap-2 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line text-lg"></i>
                Peržiūrėti Knygą
              </button>
            </div>
            
            {/* Floating elements around book */}
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-amber-400/20 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-pink-400/20 rounded-full blur-2xl animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Stats bar with count-up animation */}
      <div className="relative bg-indigo-950/50 backdrop-blur-sm border-t border-white/10" ref={statsReveal.ref}>
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            <div className="text-center space-y-2">
              <div className="text-2xl lg:text-3xl font-bold text-white mb-1">
                {statsReveal.isVisible ? `${stat1}+` : stats[0].value}
              </div>
              <div className="text-white/90 text-xs lg:text-sm font-medium leading-tight px-2">
                {stats[0].label}
              </div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-2xl lg:text-3xl font-bold text-white mb-1">
                {statsReveal.isVisible ? `${stat2}/5` : stats[1].value}
              </div>
              <div className="text-white/90 text-xs lg:text-sm font-medium leading-tight px-2">
                {stats[1].label}
              </div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-2xl lg:text-3xl font-bold text-white mb-1">
                {statsReveal.isVisible ? `${stat3}+` : stats[2].value}
              </div>
              <div className="text-white/90 text-xs lg:text-sm font-medium leading-tight px-2">
                {stats[2].label}
              </div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-2xl lg:text-3xl font-bold text-white mb-1">
                {statsReveal.isVisible ? `${stat4}%` : stats[3].value}
              </div>
              <div className="text-white/90 text-xs lg:text-sm font-medium leading-tight px-2">
                {stats[3].label}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(-5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}